import * as React from "react";
import { Activity, Clock, RefreshCw, UserRound } from "lucide-react";
import * as Effect from "effect/Effect";
import { pipe } from "effect/Function";
import { ApiClient } from "@/api/client";
import { AuditEventsResponseSchema, type AuditEvent } from "@/api/schemas/audit.schema";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingState } from "@/components/ui/loading-state";
import { ResponsiveContainer } from "@/components/layout/responsive-grid";
import { MainLayer } from "@/effects/layers";
import { runEffect } from "@/effects/runtime";
import { useActiveGroup } from "@/stores/group.store";
import { toast } from "sonner";

const eventLabels: Record<string, string> = {
  "bank_import.row_imported": "Bank import",
  "bank_import.row_linked_existing": "Bank import",
  "bank_import.rows_ignored": "Bank import",
  "bank_import.rows_unignored": "Bank import",
  "category.created": "Category",
  "category.updated": "Category",
  "category.pin_toggled": "Category",
  "category.splits_updated": "Category",
  "expense.created": "Expense",
  "expense.deleted": "Expense",
  "group.created": "Group",
  "group.updated": "Group",
  "pool.created": "Pool",
  "pool.insert_created": "Pool",
  "pool.insert_deleted": "Pool",
  "pool.weights_updated": "Pool",
  "settlement.created": "Settlement",
  "settlement.deleted": "Settlement",
};

function formatAuditDate(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function actorName(event: AuditEvent) {
  return event.actor?.name || event.actor?.email || "Unknown user";
}

export const ActivityPage: React.FC = () => {
  const { activeGroupId } = useActiveGroup();
  const [events, setEvents] = React.useState<AuditEvent[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const loadEvents = React.useCallback(async () => {
    if (!activeGroupId) return;

    setIsLoading(true);
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.get(
            `/groups/${activeGroupId}/audit-events?limit=100`,
            AuditEventsResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      const result = await runEffect(program);
      setEvents(result.events);
    } catch {
      toast.error("Failed to load activity");
    } finally {
      setIsLoading(false);
    }
  }, [activeGroupId]);

  React.useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="flex min-h-[400px] items-center justify-center text-center">
          <div>
            <h1 className="text-2xl font-bold">Activity</h1>
            <p className="mt-2 text-sm text-muted-foreground">Select a group to view its audit log.</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Activity</h1>
            <p className="mt-1 text-sm text-muted-foreground">Durable audit events for the current group.</p>
          </div>
          <Button variant="outline" onClick={loadEvents} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {isLoading && events.length === 0 ? (
          <LoadingState message="Loading activity..." />
        ) : events.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-md border border-dashed text-center">
            <div>
              <Activity className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h2 className="font-semibold">No activity yet</h2>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <Card key={event.id}>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{eventLabels[event.event_type] || event.entity_type}</Badge>
                        <span className="text-sm font-medium">{event.summary}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <UserRound className="h-3 w-3" />
                          {actorName(event)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatAuditDate(event.created_at)}
                        </span>
                      </div>
                    </div>
                    <Badge variant="secondary">{event.event_type}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};
